import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, maxLength, helpers } from '@vuelidate/validators';
import { BUTTON_TYPES } from '../constants/buttonTypes';
import { BASE_TEMPLATE_LIMITS } from '../constants/validationLimits';
import {
  validatePhoneE164,
  validateUrl,
  validateCopyCode,
  validateNoVariablesInText,
  validateUrlVariablesAtEnd,
} from './useButtonValidation';
import { useVariableValidation } from './useVariableValidation';

/**
 * Shared composable for Call to Action template validation
 * Works for both Twilio and WhatsApp platforms with configurable settings
 *
 * @param {Ref<Array>} buttons - Ref to buttons array
 * @param {ComputedRef<Array>} bodyVariables - Variables detected in body message
 * @param {ComputedRef<Array>} detectedButtonVariables - Variables detected in buttons
 * @param {ComputedRef<Object>} variableValidationRules - Variable validation rules from useVariableManagement
 * @param {ComputedRef<Object>} variableValidationState - Variable validation state from useVariableManagement
 * @param {Object} config - Platform configuration
 * @param {string} config.i18nPrefix - i18n key prefix (e.g., 'CONTENT_TEMPLATES' or 'WHATSAPP_TEMPLATES')
 * @param {Object} config.limits - Platform limits object
 * @param {Object} config.fieldMapping - Field name mapping { title: 'title'|'text', phone: 'phone'|'phone_number', code: 'code'|'example' }
 * @param {boolean} [config.showUrlExample=false] - Whether URL buttons need example field (WhatsApp)
 * @param {boolean} [config.copyCodeShowsText=true] - Whether copy code buttons show text field
 */
export function useCallToActionValidation(
  buttons,
  bodyVariables,
  detectedButtonVariables,
  variableValidationRules,
  variableValidationState,
  config = {}
) {
  const { t } = useI18n();
  const { getUniqueVariables } = useVariableValidation();

  const {
    i18nPrefix = 'CONTENT_TEMPLATES',
    limits = {},
    fieldMapping = {
      title: 'title',
      phone: 'phone',
      code: 'code',
    },
    showUrlExample = false,
    copyCodeShowsText = true,
  } = config;

  // Get field names based on platform
  const titleField = fieldMapping.title || 'title';
  const phoneField = fieldMapping.phone || 'phone';
  const codeField = fieldMapping.code || 'code';

  // Get limits with fallbacks
  const getTitleMaxLength = () =>
    limits.CALL_TO_ACTION_BUTTON_TITLE_MAX_LENGTH ??
    limits.BUTTON_TEXT_MAX_LENGTH ??
    BASE_TEMPLATE_LIMITS.CALL_TO_ACTION_BUTTON_TITLE_MAX_LENGTH;

  const getUrlMaxLength = () =>
    limits.CALL_TO_ACTION_URL_MAX_LENGTH ??
    BASE_TEMPLATE_LIMITS.CALL_TO_ACTION_URL_MAX_LENGTH;

  const getPhoneMaxLength = () =>
    limits.CALL_TO_ACTION_PHONE_MAX_LENGTH ??
    BASE_TEMPLATE_LIMITS.CALL_TO_ACTION_PHONE_MAX_LENGTH;

  const getCodeMaxLength = () =>
    limits.CALL_TO_ACTION_CODE_MAX_LENGTH ??
    limits.COPY_CODE_MAX_LENGTH ??
    BASE_TEMPLATE_LIMITS.CALL_TO_ACTION_CODE_MAX_LENGTH;

  const getMinTTL = () =>
    limits.CALL_TO_ACTION_MIN_TTL_MINUTES ??
    limits.MIN_TTL_MINUTES ??
    BASE_TEMPLATE_LIMITS.MIN_TTL_MINUTES;

  const getMaxTTL = () =>
    limits.CALL_TO_ACTION_MAX_TTL_MINUTES ??
    limits.MAX_TTL_MINUTES ??
    BASE_TEMPLATE_LIMITS.MAX_TTL_MINUTES;

  // Helper to extract variables from button based on type
  const getVariablesFromButton = button => {
    const variables = new Set();
    switch (button.type) {
      case BUTTON_TYPES.URL:
        getUniqueVariables(button.url || '').forEach(v => variables.add(v));
        break;
      case BUTTON_TYPES.COPY_CODE:
        getUniqueVariables(button[codeField] || '').forEach(v =>
          variables.add(v)
        );
        break;
      case BUTTON_TYPES.VOICE_CALL:
        getUniqueVariables(button[titleField] || '').forEach(v =>
          variables.add(v)
        );
        break;
      default:
        break;
    }
    return Array.from(variables);
  };

  // Validators
  const phoneE164Validator = helpers.withMessage(
    t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.PHONE_INVALID`),
    value => validatePhoneE164(value)
  );

  const urlValidator = helpers.withMessage(
    t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.URL_INVALID_PROTOCOL`),
    value => validateUrl(value)
  );

  const urlVariablesOnlyAtEnd = helpers.withMessage(
    t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.ERROR_URL_VARIABLES_ONLY_AT_END`),
    value => validateUrlVariablesAtEnd(value)
  );

  const copyCodeValidator = helpers.withMessage(
    t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.ERROR_CODE_INVALID_FORMAT`),
    value => validateCopyCode(value)
  );

  const noVariablesInTitle = helpers.withMessage(
    t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.ERROR_NO_VARIABLES_IN_TEXT`),
    value => validateNoVariablesInText(value, null)
  );

  // Custom validators for button variables (must be unique across buttons and not reuse body vars)
  const createButtonValidatorsForIndex = buttonIndex => ({
    buttonVariablesMustBeUnique: helpers.withMessage(
      t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.BUTTON_VARIABLES_MUST_BE_UNIQUE`),
      value => {
        if (!value) return true;

        const fieldVariables = getUniqueVariables(value);
        if (fieldVariables.length === 0) return true;

        // Check if any variables are already used in body
        const usedInBody = fieldVariables.some(v =>
          bodyVariables.value.includes(v)
        );
        if (usedInBody) return false;

        // Check if any variables are used in other buttons
        const otherButtonsVariables = buttons.value
          .filter((_, idx) => idx !== buttonIndex)
          .flatMap(btn => getVariablesFromButton(btn));

        const usedInOtherButtons = fieldVariables.some(v =>
          otherButtonsVariables.includes(v)
        );

        return !usedInOtherButtons;
      }
    ),
    buttonVariablesContinueSequence: helpers.withMessage(
      t(
        `${i18nPrefix}.CREATE.CALL_TO_ACTION.BUTTON_VARIABLES_CONTINUE_SEQUENCE`
      ),
      value => {
        if (!value) return true;

        const fieldVariables = getUniqueVariables(value);
        if (fieldVariables.length === 0) return true;

        const allUsedVariables = new Set();

        // Add body variables
        bodyVariables.value.forEach(v => allUsedVariables.add(parseInt(v, 10)));

        // Add all button variables
        buttons.value.forEach(btn => {
          getVariablesFromButton(btn).forEach(v =>
            allUsedVariables.add(parseInt(v, 10))
          );
        });

        const maxUsedVar =
          allUsedVariables.size > 0
            ? Math.max(...Array.from(allUsedVariables))
            : 0;

        const fieldVarNumbers = fieldVariables
          .map(v => parseInt(v, 10))
          .sort((a, b) => a - b);

        return fieldVarNumbers.every(varNum => {
          if (allUsedVariables.has(varNum)) return true;

          if (varNum === maxUsedVar + 1) {
            allUsedVariables.add(varNum);
            return true;
          }

          return false;
        });
      }
    ),
  });

  // Global sequence validation
  const validateGlobalSequence = computed(() =>
    helpers.withMessage(
      t(`${i18nPrefix}.CREATE.FORM.ERRORS.VARIABLES_NOT_SEQUENTIAL`),
      () => {
        const allVariables = computed(() => {
          const allVars = new Set();
          bodyVariables.value.forEach(v => allVars.add(v));
          detectedButtonVariables.value.forEach(v => allVars.add(v));
          return Array.from(allVars).sort(
            (a, b) => parseInt(a, 10) - parseInt(b, 10)
          );
        });

        if (allVariables.value.length === 0) return true;

        const numericVars = allVariables.value
          .map(v => parseInt(v, 10))
          .filter((v, i, arr) => arr.indexOf(v) === i)
          .sort((a, b) => a - b);

        for (let i = 0; i < numericVars.length; i += 1) {
          if (numericVars[i] !== i + 1) {
            return false;
          }
        }
        return true;
      }
    )
  );

  const rules = computed(() => {
    const buttonRules = {};

    buttons.value.forEach((button, index) => {
      const customValidators = createButtonValidatorsForIndex(index);

      // Title field validation (varies by button type)
      const showTitleForType =
        button.type !== BUTTON_TYPES.COPY_CODE || copyCodeShowsText;
      if (showTitleForType) {
        const titleRules = {
          required,
          maxLength: maxLength(getTitleMaxLength()),
        };

        // VOICE_CALL allows variables in title, others don't
        if (button.type === BUTTON_TYPES.VOICE_CALL) {
          Object.assign(titleRules, customValidators);
        } else {
          titleRules.noVariablesInTitle = noVariablesInTitle;
        }

        buttonRules[`button_${index}_${titleField}`] = titleRules;
      }

      // Type-specific field validation
      switch (button.type) {
        case BUTTON_TYPES.URL: {
          const urlRules = {
            required,
            maxLength: maxLength(getUrlMaxLength()),
            urlValidator,
            urlVariablesOnlyAtEnd,
            ...customValidators,
          };
          buttonRules[`button_${index}_url`] = urlRules;

          // URL example field validation (WhatsApp requirement when URL has variables)
          if (showUrlExample && button.url?.includes('{{')) {
            buttonRules[`button_${index}_example`] = {
              required: helpers.withMessage(
                t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.ERROR_EXAMPLE_REQUIRED`),
                required.$validator
              ),
            };
          }
          break;
        }

        case BUTTON_TYPES.PHONE_NUMBER:
          buttonRules[`button_${index}_${phoneField}`] = {
            required,
            maxLength: maxLength(getPhoneMaxLength()),
            phoneE164Validator,
          };
          break;

        case BUTTON_TYPES.COPY_CODE: {
          const codeRules = {
            required,
            maxLength: maxLength(getCodeMaxLength()),
            copyCodeValidator,
            ...customValidators,
          };
          buttonRules[`button_${index}_${codeField}`] = codeRules;
          break;
        }

        case BUTTON_TYPES.VOICE_CALL:
          buttonRules[`button_${index}_ttl`] = {
            minValue: helpers.withMessage(
              t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.ERROR_TTL_REQUIRED`, {
                min: getMinTTL(),
                max: getMaxTTL(),
              }),
              value => !value || value >= getMinTTL()
            ),
            maxValue: helpers.withMessage(
              t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.ERROR_TTL_REQUIRED`, {
                min: getMinTTL(),
                max: getMaxTTL(),
              }),
              value => !value || value <= getMaxTTL()
            ),
          };
          break;

        case BUTTON_TYPES.VOICE_CALL_REQUEST:
          // Only title validation, already handled above
          break;

        default:
          break;
      }
    });

    return {
      ...buttonRules,
      _globalValidation: {
        validateGlobalSequence: validateGlobalSequence.value,
      },
      ...variableValidationRules.value,
    };
  });

  const validationState = computed(() => {
    const buttonState = buttons.value.reduce((acc, button, index) => {
      acc[`button_${index}_type`] = button.type;
      acc[`button_${index}_${titleField}`] = button[titleField];
      acc[`button_${index}_url`] = button.url;
      acc[`button_${index}_${phoneField}`] = button[phoneField];
      acc[`button_${index}_${codeField}`] = button[codeField];
      acc[`button_${index}_ttl`] = button.ttl_minutes;

      // Include example field for WhatsApp URL buttons
      if (showUrlExample) {
        acc[`button_${index}_example`] = button.example;
      }

      return acc;
    }, {});

    return {
      ...buttonState,
      _globalValidation: true,
      ...variableValidationState.value,
    };
  });

  const getErrorMessage = (field, v$) => {
    // Global validation errors
    if (field === '_globalValidation') {
      if (!v$.value[field]?.$error) return '';

      const fieldValidator = v$.value[field];

      if (fieldValidator.validateGlobalSequence?.$invalid) {
        return t(`${i18nPrefix}.CREATE.FORM.ERRORS.VARIABLES_NOT_SEQUENTIAL`);
      }

      return '';
    }

    // Variable fields are handled by variable management composable
    if (field.startsWith('var_')) {
      return '';
    }

    // Button field errors
    if (!v$.value[field]?.$error) return '';

    const fieldValidator = v$.value[field];

    // Required error
    if (fieldValidator.required?.$invalid) {
      return t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.FIELD_REQUIRED`);
    }

    // Max length errors
    if (fieldValidator.maxLength?.$invalid) {
      const fieldType = field.split('_').pop();
      const limitMap = {
        [titleField]: getTitleMaxLength(),
        url: getUrlMaxLength(),
        [phoneField]: getPhoneMaxLength(),
        [codeField]: getCodeMaxLength(),
      };
      const limit = limitMap[fieldType] || 200;

      if (
        fieldType === titleField ||
        fieldType === 'text' ||
        fieldType === 'title'
      ) {
        return t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.TEXT_TOO_LONG`, {
          max: limit,
        });
      }
      return t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.FIELD_TOO_LONG`, {
        max: limit,
      });
    }

    // Specific validator errors
    if (fieldValidator.phoneE164Validator?.$invalid) {
      return t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.PHONE_INVALID`);
    }

    if (fieldValidator.urlValidator?.$invalid) {
      return t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.URL_INVALID_PROTOCOL`);
    }

    if (fieldValidator.urlVariablesOnlyAtEnd?.$invalid) {
      return t(
        `${i18nPrefix}.CREATE.CALL_TO_ACTION.ERROR_URL_VARIABLES_ONLY_AT_END`
      );
    }

    if (fieldValidator.copyCodeValidator?.$invalid) {
      return t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.ERROR_CODE_INVALID_FORMAT`);
    }

    if (fieldValidator.noVariablesInTitle?.$invalid) {
      return t(
        `${i18nPrefix}.CREATE.CALL_TO_ACTION.ERROR_NO_VARIABLES_IN_TEXT`
      );
    }

    if (fieldValidator.buttonVariablesMustBeUnique?.$invalid) {
      return t(
        `${i18nPrefix}.CREATE.CALL_TO_ACTION.BUTTON_VARIABLES_MUST_BE_UNIQUE`
      );
    }

    if (fieldValidator.buttonVariablesContinueSequence?.$invalid) {
      return t(
        `${i18nPrefix}.CREATE.CALL_TO_ACTION.BUTTON_VARIABLES_CONTINUE_SEQUENCE`
      );
    }

    if (
      fieldValidator.minValue?.$invalid ||
      fieldValidator.maxValue?.$invalid
    ) {
      return t(`${i18nPrefix}.CREATE.CALL_TO_ACTION.ERROR_TTL_REQUIRED`, {
        min: getMinTTL(),
        max: getMaxTTL(),
      });
    }

    return '';
  };

  return {
    rules,
    validationState,
    getErrorMessage,
    getVariablesFromButton,
  };
}
